import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Bem-vindo!');
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Ocorreu um erro';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="admin-login-page">
      {/* Left side - Gold accent */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#B5985A] items-center justify-center p-12">
        <div className="text-center">
          <div className="mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_reviewfunnel/artifacts/wxdmy5mp_Opini%C3%A3o%20Contacto%20Directo%20%281%29.svg"
              alt="Opinião Contacto Directo"
              className="h-16 mx-auto filter brightness-0 invert"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat' }}>
            Painel de Administração
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            Gerencie agências, consultores e acompanhe as avaliações dos seus clientes.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_reviewfunnel/artifacts/wxdmy5mp_Opini%C3%A3o%20Contacto%20Directo%20%281%29.svg"
              alt="Opinião Contacto Directo"
              className="h-12 mx-auto"
            />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#212121]" style={{ fontFamily: 'Montserrat' }}>
              Iniciar sessão
            </h2>
            <p className="text-gray-500 mt-2">
              Introduza as suas credenciais para aceder
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-[#B5985A]" />
                <span className="label-uppercase">Email</span>
              </div>
              <div className="relative">
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  className="h-12 border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-[#B5985A]" />
                <span className="label-uppercase">Palavra-passe</span>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="pr-10 h-12 border-2 border-gray-200 focus:border-[#B5985A] rounded-none"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/admin/forgot-password"
                  className="text-sm text-[#B5985A] hover:text-[#A3884A]"
                >
                  Esqueceu a palavra-passe?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-gold h-12 flex items-center justify-center gap-2"
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Entrar
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
